import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from "components/mui";
import { ContentCopyIcon } from "components/icons";
import { useNotificationProvider } from "hooks";
import { getListContacts, CreateList } from "api/canmail/CANmailAPI";

// Mirror MailingListEditor: lists are always type 1 from this UI, and a "contact"
// id is either `id` or `contact_id`. Email members carry negative ids — keep them
// so the clone reproduces the source's membership exactly.
const LIST_TYPE_ID = 1;
const getContactId = (contact) => contact.id || contact.contact_id;

/**
 * Clone an existing mailing list into a new one with the same contacts.
 *
 * The heavy lifting is done by the legacy `CreateMailList` endpoint (reached via
 * `CreateList`): it creates the list, seeds the supplied ids, and — for every
 * positive contact id — appends a `list_consent_log` row
 * ("Manually added to mail list using List Manager"). So no extra consent call is
 * needed here; reading the source members and re-creating with the same id set is
 * enough to satisfy the consent-log requirement.
 */
const CloneListModal = ({ open, onClose, sourceList, existingNames = [], onCloned }) => {
  const { notifySuccess, notifyError } = useNotificationProvider();

  const [name, setName] = useState("");
  const [cloning, setCloning] = useState(false);

  const sourceName = sourceList?.list_name || "";
  const listTypeID = sourceList?.list_type_id || LIST_TYPE_ID;

  // Pre-fill "Copy of <name>" each time the modal opens, and reset busy state.
  useEffect(() => {
    if (open) {
      setName(sourceName ? `Copy of ${sourceName}` : "");
      setCloning(false);
    }
  }, [open, sourceName]);

  const trimmedName = name.trim();
  const isDuplicate = existingNames.some(
    (existing) => (existing || "").trim().toLowerCase() === trimmedName.toLowerCase()
  );
  const nameError = !trimmedName
    ? "Please enter a list name."
    : isDuplicate
    ? "A list with that name already exists."
    : "";
  const canClone = !nameError && !cloning;

  const handleClone = async () => {
    if (!canClone || !sourceList) return;
    setCloning(true);
    try {
      // Legacy API: swallows errors and resolves to `undefined` on failure.
      // An empty array is a valid result (cloning a list with no contacts).
      const contacts = await getListContacts(sourceList.list_id, listTypeID);
      if (contacts === undefined) {
        notifyError("Failed to load the source list's contacts. Please try again.");
        return;
      }

      const ids = (contacts || []).map(getContactId);
      const result = await CreateList(trimmedName, listTypeID, JSON.stringify(ids));

      if (result != null) {
        notifySuccess(`List cloned as "${trimmedName}".`);
        onCloned?.();
        onClose?.();
      } else {
        notifyError("Failed to clone the mailing list. Please try again.");
      }
    } catch (err) {
      console.error("[CloneListModal] Error cloning list:", err);
      notifyError("Failed to clone the mailing list. Please try again.");
    } finally {
      setCloning(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && canClone) {
      event.preventDefault();
      handleClone();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={cloning ? undefined : onClose}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <ContentCopyIcon color="primary" />
        Clone Mailing List
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            This creates a new list with the same contacts as{" "}
            <strong>{sourceName || "this list"}</strong>. A consent-log entry is
            recorded for each contact added.
          </Typography>
        </Alert>

        <Box sx={{ pt: 1 }}>
          <TextField
            label="New list name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={handleKeyDown}
            error={!!nameError}
            helperText={nameError || " "}
            fullWidth
            autoFocus
            disabled={cloning}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button variant="outlined" color="primary" onClick={onClose} disabled={cloning}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleClone}
          disabled={!canClone}
          startIcon={
            cloning ? <CircularProgress size={16} color="inherit" /> : <ContentCopyIcon />
          }
        >
          {cloning ? "Cloning…" : "Clone List"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CloneListModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  sourceList: PropTypes.shape({
    list_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    list_name: PropTypes.string,
    list_type_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  existingNames: PropTypes.arrayOf(PropTypes.string),
  onCloned: PropTypes.func,
};

export default CloneListModal;
